import { Component, Input, OnChanges, SimpleChanges, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core'; // Import inject, ChangeDetectionStrategy, ChangeDetectorRef
import { PlaceService } from '../../../services/place.service';
import { PlaceResponse } from '../../../models/orders/place.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-display-place-field',
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    CommonModule,
  ],
  templateUrl: './display-place-field.component.html',
  styleUrl: './display-place-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush // Explicitly set OnPush here too (good practice for leaf components)
})
export class DisplayPlaceFieldComponent implements OnChanges {

  @Input() placeId: string | null | undefined;
  place: PlaceResponse | undefined | null;
  isLoading: boolean = false;

  private placeService = inject(PlaceService);
  private cd = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['placeId']) {
      const currentId = changes['placeId'].currentValue;
      const previousId = changes['placeId'].previousValue;

      // Fetch only if the ID has actually changed and is a valid value
      if (currentId && currentId !== previousId) {
        this.fetchPlaceData(currentId);
      } else if (changes['placeId'].isFirstChange() && currentId) {
        // Handle the very first time the input is set
        this.fetchPlaceData(currentId);
      } else if (!currentId) {
        // Handle the case where the placeId is removed (set to null/undefined)
        this.place = null;
        this.isLoading = false;
        this.cd.markForCheck(); // <-- Trigger CD when place is explicitly nulled
      }
    }
  }

  fetchPlaceData(id: string): void {
    this.isLoading = true;
    this.place = undefined; // Clear previous place data immediately
    // It's important to trigger change detection *before* the async call
    // so the spinner shows up immediately if it wasn't already visible.
    this.cd.markForCheck();

    this.placeService.getPlace(id).subscribe({
      next: (place) => {
        this.isLoading = false; // Hide spinner
        this.place = place;
        this.cd.markForCheck(); // <-- Trigger CD after successful fetch & update
      },
      error: (error) => {
        console.error(`Could not find place with ID ${id}`, error);
        this.isLoading = false; // Hide spinner
        this.place = null; // Explicitly set to null on error
        this.cd.markForCheck(); // <-- Trigger CD after fetch error & update
      }
    });
  }
}